import { useState } from "react";
import { useSnackBar } from "../../../../../../../hooks/useSnackBar";
import {
  fetchShipperUsers,
  deleteShipperUser,
  updateAccountStatus,
} from "../../../../../../../services/settings.services";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { FaRegEdit } from "react-icons/fa";
import { MdOutlineCancel } from "react-icons/md";
import { MdRemoveRedEye } from "react-icons/md";
import { FcPlus } from "react-icons/fc";
import BoltTable from "../../../../../../../Components/BoltTable/BoltTable";
import BoltButton from "../../../../../../../Components/UI/Buttons/BoltButton";
import SwitchButton from "../../../../../../../Components/SwitchButton/SwitchButton";
import Tooltip from "../../../../../../../Components/Tooltip/Tooltip";
import Confirm from "../../../../../../../Components/Popups/Confirm";
import ShipperUserDetails from "./ShipperUserDetails/ShipperUserDetails";
import EditShipperUser from "./EditShipperUser/EditShipperUser";
import AddShipperUser from "./AddShipperUser/AddShipperUser";
import ChangePassword from "./ChangePassword/ChangePassword";

const ManageShipperUsers = () => {
  const { t } = useTranslation("settings");
  const user = useSelector((state) => state.user);
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [refresh] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [detailsModal, setDetailsModal] = useState(null);
  const [editUserModal, setEditUserModal] = useState(null);
  const [addUserModal, setAddUserModal] = useState(false);
  const [changePassword, setChangePassword] = useState(null);
  const setSnack = useSnackBar();

  const handleStatusChange = async (record) => {
    const newStatus = [false, "false", "0", null, undefined].includes(
      record.account_status
    );
    setStatusUpdating(true);
    setUsers((prev) =>
      prev.data
        ? {
            ...prev,
            data: prev.data.map((u) =>
              u.id === record.id ? { ...u, account_status: newStatus } : u
            ),
          }
        : prev
    );

    const updated = await updateAccountStatus({
      id: parseInt(record.id),
      status: newStatus ? "active" : "inactive",
    });
    setStatusUpdating(false);

    if (updated.error) {
      setUsers((prev) =>
        prev.data
          ? {
              ...prev,
              data: prev.data.map((u) =>
                u.id === record.id ? { ...u, account_status: !newStatus } : u
              ),
            }
          : prev
      );
      setSnack((s) => ({
        ...s,
        open: true,
        text: updated.error.message ?? t("common:messages.somethingWentWrong"),
        severity: "error",
      }));
    } else {
      setSnack((s) => ({
        ...s,
        open: true,
        text: t(newStatus ? "helperUsers.setActive" : "helperUsers.setInactive", {
          name: updated?.data?.result?.username ?? t("helperUsers.user"),
        }),
        severity: newStatus ? "success" : "info",
      }));
    }
  };

  const handleDelete = async (record) => {
    setConfirmDelete(null);
    const response = await deleteShipperUser(record.id);
    if (response.error) {
      setSnack((s) => ({
        ...s,
        open: true,
        text: t("common:messages.somethingWentWrong"),
        severity: "error",
      }));
    } else {
      setSnack((s) => ({
        ...s,
        open: true,
        text: t("helperUsers.deleted"),
        severity: "success",
      }));
      setUsers((prev) =>
        prev.data
          ? { ...prev, data: prev.data.filter((u) => u.id !== record.id) }
          : prev
      );
    }
  };

  const fetchUsers = async (page, perPage, filter) => {
    const response = await fetchShipperUsers(page, perPage, filter);
    if (response?.data?.success === true) {
      setUsers(response?.data?.result);
    } else if (response?.error?.response?.status === 401) {
      localStorage.clear();
      navigate("/Login");
    } else {
      setSnack((s) => ({
        ...s,
        open: true,
        text: response?.error?.message ?? t("messages.somethingWentWrong"),
        severity: "error",
        vertical: "top",
        horizontal: "right",
      }));
    }
  };

  const columns = [
    {
      id: 1,
      label: t("fields.username"),
      field_name: "username",
      render: (row) => <>{row?.username ?? "-"}</>,
      width: "160px",
      fixed: "left",
    },
    {
      id: 2,
      label: t("fields.email"),
      field_name: "email",
      render: (row) => <>{row?.email ?? "-"}</>,
      width: "220px",
    },
    {
      id: 3,
      label: t("fields.role"),
      field_name: "roles",
      render: (row) => <>{row?.roles?.[0]?.name ?? "-"}</>,
      width: "140px",
    },
    {
      id: 4,
      label: t("fields.phone"),
      field_name: "phone_number",
      render: (row) => <>{row?.phone_number ?? "-"}</>,
      width: "150px",
    },
    {
      id: 5,
      label: t("fields.status"),
      field_name: "activate",
      render: (row) => (
        <SwitchButton
          onChange={() => handleStatusChange(row)}
          checked={
            ![false, "false", "0", null, undefined].includes(row.account_status)
          }
          disabled={statusUpdating}
        />
      ),
      width: "100px",
    },
    {
      id: 6,
      label: t("fields.actions"),
      field_name: "actions",
      render: (row) => (
        <div className="table_action__wrap">
          <Tooltip title={t("actions.viewDetails")}>
            <div
              className="table_action__btn"
              onClick={() => setDetailsModal(row)}
            >
              <MdRemoveRedEye size={10} />
              <span>{t("actions.details")}</span>
            </div>
          </Tooltip>
          {/* <Tooltip title="Edit User">
            <div
              className="table_action__btn"
              onClick={() => setEditUserModal(row)}
            >
              <FaRegEdit size={10} />
              <span>Edit</span>
            </div>
          </Tooltip>
          <Tooltip title="Delete User">
            <div
              className="table_action__btn"
              onClick={() => setConfirmDelete(row)}
            >
              <MdOutlineCancel size={10} />
              <span>Delete</span>
            </div>
          </Tooltip> */}
        </div>
      ),
      width: "200px",
      fixed: "right",
    },
  ];

  return (
    <>

      <Confirm
        open={!!confirmDelete}
        setOpen={setConfirmDelete}
        handleConfirm={() => handleDelete(confirmDelete)}
        text={t("helperUsers.deleteConfirm")}
      />

      <ShipperUserDetails
        open={!!detailsModal}
        data={detailsModal}
        setOpen={setDetailsModal}
        users={users?.data ?? users}
        setUsers={setUsers}
        user={user}
        setSnack={setSnack}
        setDetailsModal={setDetailsModal}
      />

      <EditShipperUser
        open={!!editUserModal}
        setOpen={setEditUserModal}
        previousData={editUserModal}
        setUsers={setUsers}
        setSnack={setSnack}
      />

      <AddShipperUser
        open={addUserModal}
        setOpen={setAddUserModal}
        setUsers={setUsers}
        setSnack={setSnack}
      />

      <ChangePassword
        open={!!changePassword}
        setOpen={setChangePassword}
        previousData={changePassword}
        setSnack={setSnack}
        setUsers={setUsers}
      />

      <div className="warehouse_clients__page_wrap">
        <BoltTable
          tableTitle={t("helperUsers.tableTitle")}
          tableId="helperUsersTable"
          message={t("helperUsers.empty")}
          searchPlaceHolder={t("helperUsers.search")}
          columns={columns}
          data={users}
          getAllData={fetchUsers}
          refresh={refresh}
          disableAutoResizing
          maxHeight="calc(100vh - 180px)"
          headerButtons={
            <div className="flex gap-1 flex-wrap">
              <BoltButton
                icon={FcPlus}
                label={t("helperUsers.createUser")}
                onClick={() => setAddUserModal(true)}
                variant="success"
                size="sm"
              />
            </div>
          }
        />
      </div>
    </>
  );
};

export default ManageShipperUsers;
